import { DemandIntentDocument } from '@models/demand/demand-intent-document.model'
import { NoticeSessionStatus } from '@models/planning/notice-session.model'
import { ProcessBidType, ProcessDisputeMode, ProcessJudgingCriteria, ProcessModality } from '@models/planning/process.model'
import { SupplierEnrollmentNotice } from '@models/supplier/notice/supplier-enrollment-notice.model'
import { Supplier } from '@models/supplier/supplier.model'
import { CompanySize } from '@models/person/company.model'
import { Address } from '@models/person/address.model'

/** Sessão do processo para o visitante (spec 048, RF 24 e 25). Sem `opened`, só status e data e hora previstas. */
export class VisitorSessionInfo {
	id: string
	status?: NoticeSessionStatus
	scheduledDate?: string
	scheduledTime?: string
	opened?: boolean
	openedAt?: string
	closedAt?: string
	bidTimerMinutes?: number
	forcedClosure?: boolean
	inReopening?: boolean
	chatRoomId?: string

	constructor(source?: Partial<VisitorSessionInfo>) {
		if (source) {
			Object.assign(this, source)
		}
	}
}

export class VisitorProcessSession {
	demandId: string
	externalCode: string
	tenantId: string
	modality?: ProcessModality
	disputeMode?: ProcessDisputeMode
	judgingCriteria?: ProcessJudgingCriteria
	bidType?: ProcessBidType
	openStageMinutes?: number
	minIntervalBetweenBids?: number
	qualificationInversion?: boolean
	budgetConfidential?: boolean
	/** A modalidade tem sessão para o visitante (Pregão, Concorrência sem avaliação técnica e Dispensa). */
	available?: boolean
	session?: VisitorSessionInfo | null

	constructor(source?: Partial<VisitorProcessSession>) {
		if (source) {
			Object.assign(this, source)
			this.session = source.session ? new VisitorSessionInfo(source.session) : null
		}
	}

	get sessionId(): string | null {
		return this.session?.id ?? null
	}

	get opened(): boolean {
		return !!this.session?.opened
	}

	get chatRoomId(): string | null {
		return this.session?.chatRoomId ?? null
	}

	/**
	 * A demanda que os painéis do participante leem (`bidBoardDemand`), montada só com o que o servidor do visitante
	 * entrega. O sigilo do orçamento vem já decidido (`budgetConfidential`): sigiloso e não revelado, ou nada.
	 */
	toDemand(): DemandIntentDocument {
		return new DemandIntentDocument({
			id: this.demandId,
			tenantId: this.tenantId,
			externalCode: this.externalCode,
			reversalQualificationPhase: this.qualificationInversion,
			process: {
				modality: this.modality,
				disputeMode: this.disputeMode,
				judgingCriteria: this.judgingCriteria,
				bidType: this.bidType,
				openStageMinutes: this.openStageMinutes,
				minIntervalBetweenBids: this.minIntervalBetweenBids,
				confidentialNature: !!this.budgetConfidential,
				budgetRevealedAt: null
			} as any,
			noticeSession: {
				id: this.session?.id,
				tenantId: this.tenantId,
				status: this.session?.status,
				openedDate: this.session?.scheduledDate,
				openedTime: this.session?.scheduledTime,
				sessionOpenedAt: this.session?.openedAt,
				sessionClosedAt: this.session?.closedAt,
				bidTimerMinutes: this.session?.bidTimerMinutes,
				forcedClosure: this.session?.forcedClosure,
				latestReopening: this.session?.inReopening ? { id: this.session.id, createdAt: this.session.openedAt } : undefined
			} as any
		})
	}
}

/** Cadastro do participante, que o servidor só devolve depois do fechamento da sessão (spec 067). */
export interface VisitorParticipantContact {
	email?: string
	phone?: string
	companySize?: CompanySize
	address?: Address
}

/** Como o servidor devolve o participante ao visitante: o nome e o documento e, com a sessão fechada, o contato. */
export interface VisitorParticipantSource extends Partial<Omit<SupplierEnrollmentNotice, 'supplier'>> {
	name?: string
	document?: string
	identityHidden?: boolean
	proposalAttachmentId?: string
	contact?: VisitorParticipantContact
}

const visitorSupplier = ({ name, document, contact }: VisitorParticipantSource = {}): Supplier =>
	({
		name,
		document,
		email: contact?.email,
		phone: contact?.phone,
		address: contact?.address,
		company: { companySize: contact?.companySize }
	}) as Supplier

/**
 * Participante da disputa para o visitante (spec 067), no modelo que a lista do participante lê. O `id` é o `ref` da
 * sessão, e os ids de arquivo são rótulos opacos. O nome e o documento seguem o RF 28 da spec 048.
 */
export class VisitorParticipant extends SupplierEnrollmentNotice {
	identityHidden: boolean
	proposalAttachmentId?: string

	constructor(source?: VisitorParticipantSource) {
		super({ ...source, supplier: visitorSupplier(source) })

		this.identityHidden = !!source?.identityHidden
		this.proposalAttachmentId = source?.proposalAttachmentId
	}
}
